import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Move, Sparkles, Plus, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const DPI = 100;
const PT = 72 / DPI;
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
const SLOT = { slot_signature: "Unterschrift", slot_stamp: "Stempel" };

function Box({ f, scale, onMove, onPick, picked }) {
  const [x0, y0, x1, y1] = f.rect;
  const start = useRef(null);
  const down = (e, resize) => {
    e.preventDefault(); e.stopPropagation(); onPick(f.fid);
    start.current = { mx: e.clientX, my: e.clientY, rect: [...f.rect], resize };
    const move = (ev) => {
      const s = start.current; const dx = (ev.clientX - s.mx) / scale; const dy = (ev.clientY - s.my) / scale;
      const r = s.rect;
      onMove(f.fid, s.resize ? [r[0], r[1], Math.max(r[0] + 15, r[2] + dx), Math.max(r[1] + 8, r[3] + dy)] : [r[0] + dx, r[1] + dy, r[2] + dx, r[3] + dy]);
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  const slot = SLOT[f.key];
  const empty = !slot && !(f.value || "").trim();
  return (
    <div onPointerDown={(e) => down(e, false)} data-testid={`traeger-pos-box-${f.fid}`} title={f.label}
      className={`absolute cursor-move select-none flex items-end overflow-hidden ${slot ? "border-2 border-dashed border-rose-500 bg-rose-500/10 justify-center" : empty ? "border border-dashed border-amber-500 bg-amber-200/20" : "border border-[#0a788c] bg-[#0a788c]/10"} ${picked ? "ring-2 ring-amber-400 z-10" : ""}`}
      style={{ left: x0 * scale, top: y0 * scale, width: (x1 - x0) * scale, height: (y1 - y0) * scale }}>
      <span className={`whitespace-nowrap leading-none px-0.5 ${slot ? "text-rose-700 font-bold text-[10px] pb-1" : empty ? "text-amber-800" : "text-[#0b1a73]"}`} style={slot ? {} : { fontSize: 8.5 * scale }}>
        {slot || f.value || f.label}
      </span>
      <span onPointerDown={(e) => down(e, true)} className="absolute right-0 bottom-0 w-2.5 h-2.5 bg-amber-500 cursor-se-resize" data-testid={`traeger-pos-resize-${f.fid}`} />
    </div>
  );
}

export default function TraegerPositionEditor({ open, onClose, form, fields, onSaved }) {
  const pages = [...new Set(fields.filter((f) => f.rect && f.key !== "unterschrift_stempel").map((f) => f.page))].sort((a, b) => a - b);
  const [pg, setPg] = useState(pages[0]);
  const [img, setImg] = useState(null);
  const [scale, setScale] = useState(1);
  const [local, setLocal] = useState(fields);
  const [changed, setChanged] = useState(new Set());
  const [picked, setPicked] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setLocal(fields); setChanged(new Set()); setPg((p) => (pages.includes(p) ? p : pages[0])); } }, [open]); // eslint-disable-line
  useEffect(() => {
    if (!open || !pg) return undefined;
    let url;
    setImg(null);
    api.get(`/traeger-forms/form/${form.form_id}/page/${pg}`, { responseType: "blob", timeout: 90000 }).then((r) => { url = URL.createObjectURL(r.data); setImg(url); }).catch(err);
    return () => url && URL.revokeObjectURL(url);
  }, [open, pg, form.form_id]);
  const onLoad = (e) => setScale(e.target.clientWidth / (e.target.naturalWidth * PT));
  const move = (fid, rect) => { setLocal((l) => l.map((f) => (f.fid === fid ? { ...f, rect } : f))); setChanged((c) => new Set(c).add(fid)); };
  const setText = (fid, value) => { setLocal((l) => l.map((f) => (f.fid === fid ? { ...f, value } : f))); setChanged((c) => new Set(c).add(fid)); };
  const del = (fid) => {
    setLocal((l) => l.map((f) => (f.fid === fid ? { ...f, value: "", _del: true } : f)));
    setChanged((c) => new Set(c).add(fid));
    setPicked(null);
  };
  const addAt = (e) => {
    if (!adding || !scale) return;
    const box = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - box.left) / scale, y = (e.clientY - box.top) / scale;
    const fid = `ex_${Math.random().toString(36).slice(2, 10)}`;
    setLocal((l) => [...l, { fid, key: "extra", page: pg, rect: [x, y, x + 180, y + 14], mode: "line", value: "Neuer Text", manual: true, label: "Eigener Text", _new: true }]);
    setChanged((c) => new Set(c).add(fid));
    setPicked(fid); setAdding(false);
  };
  const save = async () => {
    setBusy(true);
    try {
      const body = local.filter((f) => changed.has(f.fid)).map((f) => ({
        fid: f.fid, value: f.value || "", rect: f.rect.map((v) => Math.round(v * 10) / 10),
        ...(f._new ? { page: f.page } : {}), ...(f._del ? { delete: true } : {}),
      }));
      const { data } = await api.put(`/traeger-forms/form/${form.form_id}/fields`, { fields: body });
      toast.success("Änderungen gespeichert"); onSaved(data.fields); onClose();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const aiCheck = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/traeger-forms/form/${form.form_id}/ai-check`, {}, { timeout: 240000 });
      setLocal(data.fields); setChanged(new Set()); onSaved(data.fields);
      toast.success(data.moved ? `KI-Kontrolle: ${data.moved} Eintrag/Einträge korrigiert` : "KI-Kontrolle: alles sitzt richtig");
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const shown = local.filter((f) => f.page === pg && f.rect && f.key !== "unterschrift_stempel" && !f._del);
  const sel = local.find((f) => f.fid === picked && !f._del);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl rounded-none" data-testid="traeger-position-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Move size={16} /> Bearbeiten &amp; Positionen anpassen</DialogTitle>
          <DialogDescription>Eintrag anklicken → Text ändern oder löschen. Mit der Maus ziehen verschiebt, das gelbe Eck vergrößert die Fläche. „Textfeld hinzufügen" und dann auf die Stelle klicken, an der der Text stehen soll. Gelb gestrichelt = noch leer, rot gestrichelt = Unterschrift / Stempel.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          {pages.map((p) => <button key={p} onClick={() => setPg(p)} className={`px-3 h-8 text-xs border ${p === pg ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:bg-slate-100"}`} data-testid={`traeger-pos-page-${p}`}>Seite {p}</button>)}
          <button onClick={() => setAdding((v) => !v)} className={`inline-flex items-center gap-1.5 px-3 h-8 text-xs border ${adding ? "bg-amber-500 text-white border-amber-500" : "border-slate-300 hover:bg-slate-100"}`} data-testid="traeger-pos-add-btn">
            <Plus size={13} /> {adding ? "Stelle anklicken …" : "Textfeld hinzufügen"}
          </button>
          <button onClick={aiCheck} disabled={busy} className="ml-auto inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-violet-300 text-violet-800 hover:bg-violet-50 disabled:opacity-50" data-testid="traeger-ai-check-btn">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} KI-Kontrolle starten
          </button>
        </div>
        {sel && (
          <div className="flex flex-wrap items-center gap-2 border border-slate-200 bg-slate-50 px-3 py-2" data-testid="traeger-pos-edit-bar">
            <span className="text-[11px] uppercase tracking-overline text-slate-500">{sel.label || sel.key}</span>
            <input value={sel.value || ""} onChange={(e) => setText(sel.fid, e.target.value)} className="flex-1 min-w-[200px] h-8 border border-slate-300 px-2 text-sm" data-testid="traeger-pos-text-input" />
            <button onClick={() => del(sel.fid)} className="inline-flex items-center gap-1.5 h-8 px-3 text-xs border border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="traeger-pos-delete-btn"><Trash2 size={13} /> Eintrag löschen</button>
          </div>
        )}
        <div className="max-h-[68vh] overflow-auto border border-slate-200 bg-slate-100">
          {!img ? <div className="h-64 flex items-center justify-center text-sm text-slate-400"><Loader2 size={16} className="animate-spin mr-2" /> Seite wird geladen …</div> : (
            <div className={`relative ${adding ? "cursor-crosshair" : ""}`} onPointerDown={() => !adding && setPicked(null)} onClick={addAt}>
              <img src={img} alt={`Seite ${pg}`} onLoad={onLoad} className="w-full block select-none" draggable={false} data-testid="traeger-pos-page-img" />
              {shown.map((f) => <Box key={f.fid} f={f} scale={scale} onMove={move} onPick={setPicked} picked={picked === f.fid} />)}
            </div>
          )}
        </div>
        <DialogFooter>
          <span className="text-xs text-slate-500 mr-auto self-center" data-testid="traeger-pos-changed">{changed.size} geändert</span>
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 hover:bg-slate-50">Abbrechen</button>
          <button onClick={save} disabled={busy || !changed.size} className="px-4 py-2 text-sm bg-[#0a788c] text-white hover:bg-[#086373] disabled:opacity-50" data-testid="traeger-pos-save">Änderungen speichern</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
